import React, { useState, useEffect, useRef } from 'react';
import { useGetPriceHints } from '../api/carCalendarApi';
import Input from '@/components/form/input/InputField';
import { PriceHint } from '../api/carCalendarApi';

interface Props {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  carId: string;
  selectedDate?: Date;
  placeholder?: string;
}

const extractNumericPrice = (priceString: string): number => {
  const numericValue = priceString.replace(/[^0-9.]/g, '');
  return parseFloat(numericValue);
};

export const PriceInputWithHints: React.FC<Props> = ({
  value,
  onChange,
  error,
  carId,
  selectedDate,
  placeholder = 'Enter price'
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [hints, setHints] = useState<PriceHint[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number>();
  const getPriceHints = useGetPriceHints();
  const mutateAsyncRef = useRef(getPriceHints.mutateAsync);

  useEffect(() => {
    mutateAsyncRef.current = getPriceHints.mutateAsync;
  }, [getPriceHints.mutateAsync]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchHints = async () => {
      if (!selectedDate || !carId || !isFocused) {
        setHints([]);
        return;
      }

      try {
        const response = await mutateAsyncRef.current({
          date: selectedDate.toISOString().split('T')[0],
          car: carId,
        });

        const mappedHints = response.data.map(hint => ({
          source: hint.source,
          daily_price: hint.daily_price,
          avg_month_price: hint.avg_month_price,
          car_model: hint.car_model,
          active_at: hint.active_at,
          name: hint.name,
          external_car: hint.external_car,
        }));

        setHints(mappedHints);
      } catch (error) {
        console.error('Failed to fetch price hints:', error);
        setHints([]);
      }
    };

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(fetchHints, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [carId, selectedDate, isFocused]);

  const handleHintClick = (hint: PriceHint) => {
    let price: number = 0;

    // TODO: move to model
    if (typeof hint.daily_price === 'string') {
      price = extractNumericPrice(hint.daily_price);
    } else if (typeof hint.avg_month_price === 'string') {
      price = extractNumericPrice(hint.avg_month_price);
    }

    if (!isNaN(price)) {
      onChange(price.toString());
    }
    setIsFocused(false);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        type="number"
        step={0.01}
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={handleFocus}
        placeholder={placeholder}
        error={error}
      />

      {isFocused && hints.length > 0 && (
        <div
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-y-auto"
          style={{ minWidth: '200px' }}
        >
          {hints.map((hint, index) => (
            <div
              key={`${hint.source}-${index}`}
              className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center justify-between"
              onClick={() => handleHintClick(hint)}
            >
              <div>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {hint.source}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                  {hint.car_model}
                </span>
              </div>
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {hint.daily_price ? hint.daily_price : hint.avg_month_price}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
